import React, { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as action from '../../store/actions';
import { Row, Col, Card, CardBody, Button, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Alert, Spinner } from "reactstrap";
import TableContainer from "../../components/Common/TableContainer";
import PayModal from "./PayModal";
import BatchSettlementModal from "./BatchSettlementModal";
import TransactionModal from "./TransactionModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ar } from 'date-fns/locale';
import { format, parseISO, isWithinInterval, subDays } from 'date-fns';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from 'xlsx';
import { toast } from "react-toastify";

function FinancialReport() {
  const transactions = useSelector(state => state.transactions);
  const refundState = useSelector(state => state.refund);
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenDetails, setIsModalOpenDetails] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedTransactionDetails, setSelectedTransactionDetails] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [quickFilter, setQuickFilter] = useState('الكل');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [isERPInvoiceOpen, setIsERPInvoiceOpen] = useState(false);
  const [selectedForERPInvoice, setSelectedForERPInvoice] = useState(null);
  const [refundGenerationLoading, setRefundGenerationLoading] = useState(null);
  const [refundGenerationError, setRefundGenerationError] = useState(null);
  
  // Batch settlement states
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [selectedBatchInvoices, setSelectedBatchInvoices] = useState([]);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState(null);


  useEffect(() => {
    dispatch(action.fetchTransactions());
  }, [dispatch]);

  // Listen for refund generation errors from Redux
  useEffect(() => {
    if (refundState?.error) {
      const errorMessage = refundState.error;
      setRefundGenerationError(errorMessage);
      setRefundGenerationLoading(null);
      
      // Show error toast with larger size for long messages
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 8000,
        style: { minWidth: "400px" },
      });
    }
  }, [refundState?.error]);

  // Listen for refund generation success from Redux
  useEffect(() => {
    if (refundState?.loading === false && !refundState?.error && refundState?.refunds?.length > 0) {
      // Show success toast
      toast.success("تم إنشاء طلب الاسترجاع بنجاح", {
        position: "top-center",
        autoClose: 3000,
      });
      
      // Refresh transactions to update UI
      setTimeout(() => {
        dispatch(action.fetchTransactions());
      }, 500);
    }
  }, [refundState?.loading, refundState?.refunds?.length]);




  const handleRowClickDetails = transaction => {
    setSelectedTransactionDetails(transaction);
    toggleModalDetails();
  };

  const handleRowClick = transaction => {
    setSelectedTransaction(transaction);
    toggleModal();
  };

  // Prefer an existing ERP commission invoice before allowing refund generation
  const getErpInvoiceId = (invoice = {}) => {
    const pick = [
      invoice.commission_erp_id,
      invoice.erp_invoice_id,
      invoice["erp_invoice_id "],
      invoice.booking?.erp_invoice_id,
      invoice.booking?.["erp_invoice_id "],
    ].map((v) => (typeof v === "string" ? v.trim() : v)).find(Boolean);
    return pick || "";
  };

  const applyQuickFilter = (range) => {
    setQuickFilter(range);
    let start = null;
    let end = null;
    
    switch(range) {
      case 'اليوم':
        start = new Date();
        start.setHours(0, 0, 0, 0);
        end = new Date();
        break;
      case 'الأسبوع':
        start = subDays(new Date(), 7);
        end = new Date();
        break;
      case 'الشهر':
        start = subDays(new Date(), 30);
        end = new Date();
        break;
      default:
        // 'الكل' أو غير ذلك
        break;
    }
    
    setDateRange([start, end]);
  };

  const applyDateFilter = () => {
    toggleDateRangeModal();
  };

  const clearFilters = () => {
    setDateRange([null, null]);
    setQuickFilter('الكل');
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Filter for pending payments only - use payment_status not paid_status
      if (transaction.payment_status !== 'pending') return false;
      
      // Date range filtering if set
      if (startDate && endDate) {
        try {
          let transactionDate;
          
          if (transaction.created_at?.seconds) {
            transactionDate = new Date(transaction.created_at.seconds * 1000);
          } else if (transaction.created_at) {
            transactionDate = parseISO(transaction.created_at);
          } else {
            return false;
          }
          
          return isWithinInterval(transactionDate, {
            start: startDate,
            end: endDate
          });
        } catch (error) {
          console.error('خطأ في تصفية التاريخ:', error);
          return false;
        }
      }
      
      return true;
    });
  }, [transactions, startDate, endDate]);

  // Filter for flagged invoices (needs_refund = true)
  const flaggedInvoicesForRefund = useMemo(() => {
    return (transactions || []).filter((invoice) => {
      return invoice.needs_refund === true && invoice.refund_status === "pending";
    });
  }, [transactions]);

  // Handle generating refund from flagged invoice
  const handleGenerateRefund = (invoice) => {

    
    // Try multiple ID field names
    const invoiceId = invoice?.id || invoice?.invoice_id || Object.keys(invoice || {}).find(k => k === 'id');
    
    if (!invoiceId) {
      console.error("❌ Invoice ID not found. Available fields:", Object.keys(invoice || {}));
      const errorMsg = "فشل: لم يتم العثور على معرّف الفاتورة";
      setRefundGenerationError(errorMsg);
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }
    


    const erpInvoiceId = getErpInvoiceId(invoice);
    if (!erpInvoiceId) {
      const errorMsg = "يجب إنشاء فاتورة ERP أولاً قبل إنشاء طلب الاسترجاع";
      setRefundGenerationError(errorMsg);
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 6000,
      });
      return;
    }
    
    setRefundGenerationLoading(invoiceId);
    setRefundGenerationError(null);

    dispatch(
      action.generateRefundFromFlaggedInvoiceRequest({
        ...invoice,
        id: invoiceId,
      })
    );

    // Clear loading state after a delay (saga will handle success/error)
    setTimeout(() => {
      setRefundGenerationLoading(null);
    }, 2000);
  };

  // Format transaction date
  const formatTransactionDate = (date) => {
    if (!date) return 'غير متوفر';
    
    try {
      let parsedDate;
      
      if (typeof date === 'object' && 'seconds' in date) {
        parsedDate = new Date(date.seconds * 1000);
      } 
      else if (typeof date === 'string') {
        parsedDate = parseISO(date);
      }
      else if (date instanceof Date) {
        parsedDate = date;
      } else {
        return 'تاريخ غير صالح';
      }
      
      return format(parsedDate, 'yyyy/MM/dd HH:mm', { locale: ar });
    } catch (error) {
      console.error('خطأ في تحويل التاريخ:', error);
      return 'تاريخ غير صالح';
    }
  };

  // Toggle functions
  const toggleModalDetails = () => setIsModalOpenDetails(!isModalOpenDetails);
  const toggleModal = () => setIsModalOpen(!isModalOpen);
  const toggleDateRangeModal = () => setIsDateRangeModalOpen(!isDateRangeModalOpen);
  const toggleExportDropdown = () => setExportDropdownOpen(prevState => !prevState);
  const toggleERPInvoice = () => setIsERPInvoiceOpen((s) => !s);
  const toggleBatchModal = () => setIsBatchModalOpen(!isBatchModalOpen);

  const openERPInvoiceModal = (transaction) => {
    setSelectedForERPInvoice(transaction);
    setIsERPInvoiceOpen(true);
  };

  // Batch settlement functions
  const handleOpenBatchSettlement = (provider, invoices) => {
    setSelectedServiceProvider(provider);
    setSelectedBatchInvoices(invoices);
    setIsBatchModalOpen(true);
  };

  // Group pending invoices by service provider
  const groupedByProvider = useMemo(() => {
    const groups = {};

    const isRefundMarked = (invoice) => {
      const refundStatuses = ["pending", "approved", "completed", "refunded"];
      return (
        invoice?.needs_refund === true ||
        refundStatuses.includes(invoice?.refund_status) ||
        Boolean(invoice?.refund_request_id)
      );
    };
    
    // Include invoices that are not already batched and not refund-marked
    filteredTransactions.forEach(invoice => {
      const providerId = invoice.serviceProviders?.id;
      
      if (providerId && !invoice.batch_settlement_id && !isRefundMarked(invoice)) {
        if (!groups[providerId]) {
          groups[providerId] = {
            provider: invoice.serviceProviders,
            invoices: []
          };
        }
        groups[providerId].invoices.push(invoice);
      }
    });

    return Object.values(groups).filter(g => g.invoices.length > 0);
  }, [filteredTransactions]);

  // Excel Export Functions
  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredTransactions.map(t => ({
        'تاريخ العملية': formatTransactionDate(t.created_at),
        'مقدم الخدمة': t.serviceProviders?.salon_name || 'غير متوفر',
        'المبلغ (ر.س)': t.total,
        'حالة الدفع': 'قيد الانتظار', // Since we're filtering for pending payments
        'تاريخ التنفيذ': t.booking_execute_date ? formatTransactionDate(t.booking_execute_date) : 'غير محدد',
        'ملاحظات': t.comment || 'لا يوجد'
      }))
    );
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "المستحقات المالية");
    XLSX.writeFile(workbook, "المستحقات_المالية.xlsx");
  };

  const exportSingleToExcel = (transaction) => {
    const worksheet = XLSX.utils.json_to_sheet([{
      'رقم العملية': transaction.id,
      'تاريخ العملية': formatTransactionDate(transaction.created_at),
      'مقدم الخدمة': transaction.serviceProviders?.salon_name || 'غير متوفر',
      'المبلغ (ر.س)': transaction.total,
      'حالة الدفع': 'قيد الانتظار',
      'تاريخ التنفيذ': transaction.booking_execute_date ? formatTransactionDate(transaction.booking_execute_date) : 'غير محدد',
      'ملاحظات': transaction.comment || 'لا يوجد'
    }]);
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "فاتورة مستحقات");
    XLSX.writeFile(workbook, `فاتورة_${transaction.serviceProviders?.salon_name || transaction.id}.xlsx`);
  };

  // PDF Export Functions
  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(18);
    doc.text("المستحقات المالية", 148.5, 20, { align: 'center' });
    
    if (startDate || endDate) {
      doc.setFontSize(12);
      doc.text(
        `الفترة: ${startDate ? format(startDate, 'yyyy/MM/dd', { locale: ar }) : '...'} إلى ${endDate ? format(endDate, 'yyyy/MM/dd', { locale: ar }) : '...'}`,
        148.5,
        30,
        { align: 'center' }
      );
    }
    
    const headers = [
      ['رقم', 'تاريخ العملية', 'مقدم الخدمة', 'المبلغ (ر.س)', 'حالة الدفع', 'تاريخ التنفيذ']
    ];
    
    const data = filteredTransactions.map((t, index) => [
      index + 1,
      formatTransactionDate(t.created_at),
      t.serviceProviders?.salon_name || 'غير متوفر',
      t.total,
      'قيد الانتظار',
      t.booking_execute_date ? formatTransactionDate(t.booking_execute_date) : 'غير محدد'
    ]);
    
    autoTable(doc, {
      head: headers,
      body: data,
      startY: 40,
      styles: {
        font: 'helvetica',
        fontSize: 10,
        halign: 'center'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 20 },
        4: { cellWidth: 25 },
        5: { cellWidth: 30 }
      }
    });
    
    doc.save("المستحقات_المالية.pdf");
  };

  const exportSingleToPDF = (transaction) => {
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text("فاتورة مستحقات", 105, 15, { align: 'center' });
    
    doc.setFontSize(12);
    let y = 30;
    
    const details = [
      ['رقم العملية', transaction.id],
      ['تاريخ العملية', formatTransactionDate(transaction.created_at)],
      ['مقدم الخدمة', transaction.serviceProviders?.salon_name || 'غير متوفر'],
      ['المبلغ', `${transaction.total} ر.س`],
      ['حالة الدفع', 'قيد الانتظار'],
      ['تاريخ التنفيذ', transaction.booking_execute_date ? formatTransactionDate(transaction.booking_execute_date) : 'غير محدد'],
      ['ملاحظات', transaction.comment || 'لا يوجد']
    ];
    
    details.forEach(([label, value]) => {
      doc.text(`${label}: ${value}`, 20, y);
      y += 10;
    });
    
    // Add payment instructions
    doc.setFontSize(10);
    doc.text("يرجى تسديد المبلغ المستحق خلال 3 أيام عمل", 105, y + 15, { align: 'center' });
    
    doc.save(`فاتورة_${transaction.serviceProviders?.salon_name || transaction.id}.pdf`);
  };


  const columns = useMemo(
    () => [
      {
        Header: "تاريخ العملية",
        accessor: "created_at",
        Cell: ({ value }) => formatTransactionDate(value),
      },
      {
        Header: "مقدم الخدمة",
        accessor: "serviceProviders.salon_name",
        Cell: ({ value }) => value || 'غير متوفر',
      },
      {
        Header: "غرامة إلغاء",
        accessor: "fine_deductions_total",
        Cell: ({ value }) => {
          const fine = Number(value || 0);
          if (fine <= 0) {
            return <Badge color="light" className="text-muted">لا يوجد</Badge>;
          }
          return (
            <div className="d-flex flex-column align-items-start">
              <Badge color="danger" className="mb-1">
                -{fine.toFixed(2)} ر.س
              </Badge>
              <small className="text-muted">مخصومة من الفاتورة</small>
            </div>
          );
        },
      },
      {
        Header: "الصافي بعد الغرامة",
        accessor: "net_after_fines",
        Cell: ({ value, row }) => {
          const val = value !== undefined ? Number(value) : Number(row.original.total_after_discount || 0);
          return `${val.toFixed(2)} ر.س`;
        },
      },
      // {
      //   Header: "المبلغ",
      //   accessor: "total",
      //   Cell: ({ value }) => `${value} ر.س`,
      // },
      {
        Header: "حالة الدفع",
        accessor: "payment_status", // Changed from paid_status
        Cell: ({ value }) => (
          <Badge color="warning" className="font-size-12">
            <i className="bx bx-time-five align-middle me-1" />
            قيد الانتظار
          </Badge>
        ),
      },
      {
        Header: "عرض التفاصيل",
        accessor: "details",
        Cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              color="primary"
              size="sm"
              onClick={() => handleRowClickDetails(row.original)}
              className="py-1 px-2"
            >
              عرض التفاصيل
            </Button>
            {/* <Button
              color="success"
              size="sm"
              onClick={() => exportSingleToPDF(row.original)}
              className="py-1 px-2"
            >
              PDF
            </Button> */}
            <Button
              color="info"
              size="sm"
              onClick={() => exportSingleToExcel(row.original)}
              className="py-1 px-2"
            >
              Excel
            </Button>
          </div>
        ),
      },
      {
        Header: "دفع المستحق",
        accessor: "payment",
        Cell: ({ row }) => (
          <Button
            color="success"
            size="sm"
            onClick={() => handleRowClick(row.original)}
            className="py-1 px-3"
          >
            إنشاء فاتورة
          </Button>
        ),
      },
      // {
      //   Header: "فاتورة ERP",
      //   accessor: "erp_invoice_btn",
      //   Cell: ({ row }) => {
      //     const t = row.original;
      //     const getErpId = () => {
      //       // try multiple keys and trim
      //       const fromRoot = (t.erp_invoice_id || t["erp_invoice_id "] || "").toString().trim();
      //       const fromBooking = (t.booking?.erp_invoice_id || t.booking?.["erp_invoice_id "] || "").toString().trim();
      //       return fromRoot || fromBooking || "";
      //     };
      //     const erpId = getErpId();
      //     return (
      //       <Button
      //         color="primary"
      //         size="sm"
      //         onClick={() => openERPInvoiceModal(t)}
      //         disabled={!erpId}
      //       >
      //         {erpId ? "عرض فاتورة ERP" : "لا يوجد رقم فاتورة"}
      //       </Button>
      //     );
      //   },
      // },
    ],
    []
  );

  // Columns for flagged invoices (marked for refund)
  const flaggedInvoiceColumns = useMemo(
    () => [
      {
        Header: "تاريخ الإلغاء",
        accessor: "refund_initiated_at",
        Cell: ({ value }) => formatTransactionDate(value),
      },
      {
        Header: "مقدم الخدمة",
        accessor: "serviceProviders.salon_name",
        Cell: ({ value }) => value || 'غير متوفر',
      },
      {
        Header: "سبب الاسترجاع",
        accessor: "refund_reason",
        Cell: ({ value }) => value || "إلغاء من قبل مقدم الخدمة",
      },
      {
        Header: "المبلغ المستحق",
        accessor: "total_platform_revenue",
        Cell: ({ value }) => `${Number(value || 0).toFixed(2)} ر.س`,
      },
      {
        Header: "حالة الاسترجاع",
        accessor: "refund_status",
        Cell: ({ value }) => (
          <Badge color="warning">
            <i className="bx bx-time-five align-middle me-1" />
            {value === "pending" ? "قيد المراجعة" : value}
          </Badge>
        ),
      },
      {
        Header: "الإجراءات",
        accessor: "actions",
        Cell: ({ row }) => {
          const invoice = row.original;
          const invoiceId = invoice?.id || invoice?.invoice_id;
          const isProcessing = refundGenerationLoading === invoiceId;
          const hasErpInvoice = Boolean(getErpInvoiceId(invoice));

          return (
            <Button
              color="success"
              size="sm"
              onClick={() => handleGenerateRefund(invoice)}
              // disabled={isProcessing || !!invoice.refund_request_id || !hasErpInvoice}
              className="py-1 px-3"
              title={
                !hasErpInvoice
                  ? "يجب إنشاء فاتورة ERP أولاً"
                  : invoiceId
                  ? `Invoice ID: ${invoiceId}`
                  : "No invoice ID"
              }
            >
    
              {isProcessing ? (
                <>
                  <Spinner size="sm" color="light" className="me-2" />
                  جاري المعالجة...
                </>
              ) : invoice.refund_request_id ? (
                "تم إنشاء الاسترجاع"
              ) : !hasErpInvoice ? (
                "أنشئ فاتورة ERP أولاً"
              ) : (
                "إنشاء طلب استرجاع"
              )}
            </Button>
          );
        },
      },
    ],
    [refundGenerationLoading]
  );

  return (
    <div className="page-content" dir="rtl">
      <Row>
        <Col lg="12">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
                <div>
                  <h4 className="card-title mb-1">التقارير المالية</h4>
                  <small className="text-muted">إدارة المستحقات، الفواتير المميزة للاسترجاع، والتصدير السريع.</small>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <Badge color="primary" pill className="px-3 py-2">
                    المستحقات: {filteredTransactions.length}
                  </Badge>
                  <Badge color="warning" pill className="px-3 py-2">
                    مميزة للاسترجاع: {flaggedInvoicesForRefund.length}
                  </Badge>
                </div>
              </div>

              {/* Filters & export */}
              <Card className="mb-3 border-0 shadow-sm">
                <CardBody className="py-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="text-muted me-2">تصفية سريعة:</span>
                    <Button
                      color={quickFilter === 'الكل' ? 'primary' : 'light'}
                      size="sm"
                      onClick={() => applyQuickFilter('الكل')}
                    >
                      الكل
                    </Button>
                    <Button
                      color={quickFilter === 'اليوم' ? 'primary' : 'light'}
                      size="sm"
                      onClick={() => applyQuickFilter('اليوم')}
                    >
                      اليوم
                    </Button>
                    <Button
                      color={quickFilter === 'الأسبوع' ? 'primary' : 'light'}
                      size="sm"
                      onClick={() => applyQuickFilter('الأسبوع')}
                    >
                      الأسبوع
                    </Button>
                    <Button
                      color={quickFilter === 'الشهر' ? 'primary' : 'light'}
                      size="sm"
                      onClick={() => applyQuickFilter('الشهر')}
                    >
                      الشهر
                    </Button>
                    <Button color="secondary" size="sm" onClick={toggleDateRangeModal}>
                      نطاق مخصص
                    </Button>
                    {(startDate || endDate) && (
                      <Button color="link" size="sm" onClick={clearFilters}>
                        مسح الفلتر
                      </Button>
                    )}

                    <div className="ms-auto d-flex gap-2 align-items-center">
                      <Button color="success" size="sm" onClick={exportToExcel}>
                        تصدير كل المستحقات (Excel)
                      </Button>
                    </div>
                  </div>

                  {(startDate || endDate) && (
                    <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                      <Badge color="info" className="me-2">
                        {startDate ? format(startDate, 'yyyy/MM/dd', { locale: ar }) : '...'} 
                        {' → '}
                        {endDate ? format(endDate, 'yyyy/MM/dd', { locale: ar }) : '...'}
                      </Badge>
                      <small className="text-muted">
                        {filteredTransactions.length} معاملة ضمن النطاق الزمني المحدد
                      </small>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Batch Settlement Section - Group invoices by service provider */}
              {groupedByProvider.length > 0 && (
                <div className="mb-4">
                  <Card className="border-success bg-light-subtle shadow-sm">
                    <CardBody>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <div>
                          <h5 className="card-title mb-1">
                            <i className="bx bx-layer me-2"></i>
                            التسويات المجمعة
                          </h5>
                          <small className="text-muted">
                            إنشاء فاتورة ERP واحدة لكل مقدم خدمة تشمل جميع الفواتير المعلقة
                          </small>
                        </div>
                        <Badge color="success" pill className="px-3 py-2">
                          {groupedByProvider.length} مقدم خدمة
                        </Badge>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover table-sm">
                          <thead className="table-light">
                            <tr>
                              <th>مقدم الخدمة</th>
                              <th className="text-center">عدد الفواتير</th>
                              <th className="text-center">إجمالي المبلغ</th>
                              <th className="text-center">الفترة</th>
                              <th className="text-center">الإجراء</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedByProvider.map(({ provider, invoices }) => {
                              const totalAmount = invoices.reduce((sum, inv) => 
                                sum + (Number(inv.total_after_discount || 0)), 0
                              );
                              
                              const dates = invoices.map(inv => {
                                if (inv.created_at?.seconds) {
                                  return new Date(inv.created_at.seconds * 1000);
                                } else if (typeof inv.created_at === 'string') {
                                  return new Date(inv.created_at);
                                }
                                return new Date();
                              }).sort((a, b) => a - b);

                              const startDateStr = format(dates[0], 'yyyy/MM/dd');
                              const endDateStr = format(dates[dates.length - 1], 'yyyy/MM/dd');

                              return (
                                <tr key={provider.id}>
                                  <td>
                                    <strong>{provider.salon_name}</strong>
                                    <br />
                                    <small className="text-muted">{provider.mobile || 'N/A'}</small>
                                  </td>
                                  <td className="text-center">
                                    <Badge color="info" pill>{invoices.length}</Badge>
                                  </td>
                                  <td className="text-center">
                                    <strong>{totalAmount.toFixed(2)} ريال</strong>
                                  </td>
                                  <td className="text-center">
                                    <small>{startDateStr} - {endDateStr}</small>
                                  </td>
                                  <td className="text-center">
                                    <Button
                                      color="success"
                                      size="sm"
                                      onClick={() => handleOpenBatchSettlement(provider, invoices)}
                                      className="d-flex align-items-center mx-auto"
                                    >
                                      <i className="bx bx-check-circle me-1"></i>
                                      إنشاء تسوية مجمعة
                                    </Button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      <Alert color="info" className="mt-3 mb-0">
                        <i className="bx bx-info-circle me-2"></i>
                        سيتم إنشاء <strong>فاتورة ERP واحدة لكل مقدم خدمة</strong> تشمل جميع العمولات والغرامات للفترة المحددة.
                      </Alert>
                    </CardBody>
                  </Card>
                </div>
              )}

              {/* Flagged Invoices Section (marked for refund) */}
              {flaggedInvoicesForRefund.length > 0 && (
                <div className="mb-4">
                  <Card className="border-warning bg-light-subtle shadow-sm">
                    <CardBody>
                      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <Badge color="warning" className="me-2" pill>
                            <i className="bx bx-alert-triangle align-middle me-1" />
                            {flaggedInvoicesForRefund.length}
                          </Badge>
                          <div>
                            <h5 className="card-title mb-1">فواتير مميزة للاسترجاع</h5>
                            <small className="text-muted">سجّل الموافقات أو ابدأ إنشاء طلب الاسترجاع من هنا.</small>
                          </div>
                        </div>
                        <Badge color="secondary" pill className="px-3 py-2">
                          جاهزة للتعامل
                        </Badge>
                      </div>
                      
                      {refundGenerationError && (
                        <Alert color="danger" className="mb-3">
                          <i className="bx bx-error-circle align-middle me-2" />
                          {refundGenerationError}
                        </Alert>
                      )}
                      
                      <TableContainer
                        columns={flaggedInvoiceColumns}
                        data={flaggedInvoicesForRefund}
                        customPageSize={5}
                        className="custom-header-css"
                      />
                    </CardBody>
                  </Card>
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">المستحقات المعلقة</h5>
                  <small className="text-muted">فواتير بانتظار الدفع أو الإصدار.</small>
                </div>
                <Badge color="primary" pill className="px-3 py-2">
                  {filteredTransactions.length} سجل
                </Badge>
              </div>

              <TableContainer
                columns={columns}
                data={filteredTransactions}
                customPageSize={10}
                className="custom-header-css"
              />
              
              <PayModal
                isOpen={isModalOpen}
                toggleModal={toggleModal}
                transaction={selectedTransaction}
              />

              <BatchSettlementModal
                isOpen={isBatchModalOpen}
                toggleModal={toggleBatchModal}
                selectedInvoices={selectedBatchInvoices}
                serviceProvider={selectedServiceProvider}
              />
              
              <TransactionModal
                isOpen={isModalOpenDetails}
                toggleModal={toggleModalDetails}
                transaction={selectedTransactionDetails}
              />
              
              <ERPInvoiceModal
                isOpen={isERPInvoiceOpen}
                toggle={toggleERPInvoice}
                transaction={selectedForERPInvoice}
              />
              
              {/* Custom date range modal */}
              <Modal isOpen={isDateRangeModalOpen} toggle={toggleDateRangeModal} dir="rtl">
                <ModalHeader toggle={toggleDateRangeModal}>
                  اختر نطاق التاريخ
                </ModalHeader>
                <ModalBody>
                  <DatePicker
                    selectsRange
                    startDate={startDate}
                    endDate={endDate}
                    onChange={(update) => setDateRange(update)}
                    inline
                    locale={ar}
                    dateFormat="yyyy/MM/dd"
                    isClearable
                  />
                </ModalBody>
                <ModalFooter>
                  <Button color="primary" onClick={applyDateFilter}>
                    تطبيق
                  </Button>
                  <Button color="secondary" onClick={toggleDateRangeModal}>
                    إلغاء
                  </Button>
                </ModalFooter>
              </Modal>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}


export default FinancialReport;

// Modal to show ERP invoice using erp_invoice_id with QR and items
const ERPInvoiceModal = ({ isOpen, toggle, transaction }) => {
  const [loading, setLoading] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const baseUrl = "https://node-server-eta-coral.vercel.app";

  // Normalize and sanitize erp id (trim spaces/tabs)
  const getErpId = (t) => {
    if (!t) return "";
    const sanitize = (v) => (typeof v === "string" ? v.trim() : (v ?? "")).toString().trim();
    const root = sanitize(t.erp_invoice_id || t["erp_invoice_id "] || "");
    const fromBooking = sanitize(t.booking?.erp_invoice_id || t.booking?.["erp_invoice_id "] || "");
    return root || fromBooking || "";
  };

  const fetchInvoice = async (invoiceId, signal) => {
    try {
      setLoading(true);
      setError(null);
      setInvoice(null);
      setQrImageUrl(null);

      if (!invoiceId) {
        setError("لا يحتوي السجل على رقم فاتورة ERP.");
        return;
      }

      const res = await fetch(`${baseUrl}/invoice/${encodeURIComponent(invoiceId)}`, {
        headers: { "Content-Type": "application/json" },
        signal,
      });
      const json = await res.json();

      const inv = json?.data || (Array.isArray(json?.data) ? json.data[0] : null) || json;
      if (!inv || !inv.name) {
        setError("لم يتم العثور على الفاتورة في ERP.");
        return;
      }
      setInvoice(inv);

      if (inv.ksa_einv_qr && !signal?.aborted) {
        try {
          const imgRes = await fetch(`${baseUrl}/erp-image?path=${encodeURIComponent(inv.ksa_einv_qr)}`, { signal });
          if (imgRes.ok) {
            const blob = await imgRes.blob();
            if (!signal?.aborted) setQrImageUrl(URL.createObjectURL(blob));
          }
        } catch {
          // ignore QR fetch failure
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setError(`خطأ أثناء جلب الفاتورة: ${e.message}`);
      }
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const controller = new AbortController();
    const id = getErpId(transaction);
    fetchInvoice(id, controller.signal);
    return () => controller.abort();
  }, [isOpen, transaction]); // fetch when modal opens or transaction changes

  // dedicated cleanup for QR object URL
  useEffect(() => {
    return () => {
      if (qrImageUrl) URL.revokeObjectURL(qrImageUrl);
    };
  }, [qrImageUrl]);

  if (!transaction) return null;

  // Derive display fields (safe fallbacks)
  const providerName =
    transaction?.serviceProviders?.salon_name ||
    transaction?.service_provider_name ||
    "غير متوفر";
  const providerMobile =
    transaction?.serviceProviders?.mobile ||
    transaction?.service_provider_mobile ||
    "غير متوفر";
  const userName =
    transaction?.app_user_id?.username ||
    transaction?.app_user_id?.full_name ||
    transaction?.user_name ||
    "غير متوفر";
  const userMobile =
    transaction?.app_user_id?.mobile ||
    transaction?.app_user_moble ||
    transaction?.app_user_mobile ||
    "غير متوفر";

  const handleCopy = async () => {
    const id = invoice?.name;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  const openInERP = () => {
    if (!invoice?.name) return;
    const url = `https://dabtesting.frappe.cloud/app/sales-invoice/${encodeURIComponent(invoice.name)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>فاتورة ERP</ModalHeader>
      <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* App logo exactly like before */}
        <img
          src="/logo.png"
          alt="Company Logo"
          style={{ height: 60, objectFit: "contain" }}
          className="mb-4 d-block mx-auto"
        />

        {/* Provider and App User details */}
        <Row className="mb-3">
          <Col md={6}>
            <div className="mb-2">
              <h5>معلومات مقدم الخدمة</h5>
              <p className="mb-1"><strong>اسم مقدم الخدمة:</strong> {providerName}</p>
              <p className="mb-1"><strong>رقم الجوال:</strong> {providerMobile}</p>
            </div>
          </Col>
          <Col md={6}>
            <div className="mb-2">
              <h5>معلومات العميل</h5>
              <p className="mb-1"><strong>اسم العميل:</strong> {userName}</p>
              <p className="mb-1"><strong>رقم الجوال:</strong> {userMobile}</p>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">جاري تحميل بيانات الفاتورة...</div>
          </div>
        ) : error ? (
          <Alert color="danger" className="mb-0">{error}</Alert>
        ) : invoice ? (
          <>
            <Card className="mb-3">
              <CardBody>
                <Row>
                  <Col md={6}>
                    <p><strong>رقم الفاتورة:</strong> {invoice.name || "N/A"}</p>
                    <p><strong>اسم العميل:</strong> {invoice.customer_name || invoice.customer || "N/A"}</p>
                    <p><strong>الشركة:</strong> {invoice.company || "N/A"}</p>
                  </Col>
                  <Col md={6} className="text-end">
                    <p><strong>التاريخ:</strong> {invoice.posting_date || "N/A"}</p>
                    <p><strong>المجموع:</strong> {invoice.grand_total ?? 0} {invoice.currency || "ر.س"}</p>
                    {qrImageUrl ? (
                      <img
                        src={qrImageUrl}
                        alt="ZATCA QR"
                        style={{ width: 128, height: 128, objectFit: "contain" }}
                      />
                    ) : (
                      <small className="text-muted">لا يوجد رمز QR</small>
                    )}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {Array.isArray(invoice.items) && invoice.items.length > 0 && (
              <Card className="mb-3">
                <CardBody>
                  <h6 className="mb-3">المنتجات/الخدمات</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>الصنف</th>
                          <th>الكمية</th>
                          <th className="text-end">السعر</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.items.map((it) => (
                          <tr key={it.name || `${it.item_code}-${Math.random()}`}>
                            <td>{it.item_name || it.description || it.item_code || "—"}</td>
                            <td>{it.qty ?? 0}</td>
                            <td className="text-end">{it.rate ?? 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            )}
          </>
        ) : (
          <Alert color="info" className="mb-0">لا توجد بيانات للعرض.</Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          إغلاق
        </Button>
        <Button color="light" onClick={handleCopy} disabled={!invoice?.name} aria-label="copy-invoice-id">
          {copied ? "تم النسخ" : "نسخ رقم الفاتورة"}
        </Button>
        <Button color="primary" onClick={openInERP} disabled={!invoice?.name} aria-label="open-in-erp">
          فتح في ERP
        </Button>
      </ModalFooter>
    </Modal>
  );
};